/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import * as tf from '@tensorflow/tfjs';

import toastr from 'toastr';

import {
  Badge,
  Popover,
  PopoverBody,
  Button,
  Alert,
} from 'reactstrap';

import { get } from '../../helpers/api_helper';

function TagSelector(props) {
  const {
    initialTags, onUpdate, allowCreate, id, predictorTitle, t,
  } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tags, setTags] = useState(initialTags);
  const [matches, setMatches] = useState([]);
  const [query, setQuery] = useState('');
  const [ppv, setPpv] = useState(false);

  const modelRef = useRef(null);
  const metadataRef = useRef(null);
  const predictorRanForRef = useRef(null);

  const handleTagChange = (ltags) => {
    onUpdate(ltags);
  };

  function normalizeTitle(title) {
    return String(title || '')
      .toLowerCase()
      .replace(/[^a-z0-9\-/.\s]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokenize(title) {
    return normalizeTitle(title)
      .split(' ')
      .filter(Boolean);
  }

  function padSequence(seq, maxLen) {
    if (seq.length >= maxLen) return seq.slice(0, maxLen);
    return seq.concat(new Array(maxLen - seq.length).fill(0));
  }

  function mergeUniqueTags(existingTags, incomingTags) {
    const byId = new Map();

    existingTags.forEach((tag) => {
      if (tag.id != null) {
        byId.set(String(tag.id), tag);
      }
    });

    const withoutIds = existingTags.filter((tag) => tag.id == null);

    incomingTags.forEach((tag) => {
      if (tag.id != null) {
        byId.set(String(tag.id), tag);
      }
    });

    return [...withoutIds, ...Array.from(byId.values())];
  }

  async function ensurePredictorLoaded() {
    if (modelRef.current && metadataRef.current) {
      return;
    }

    const [model, metadata] = await Promise.all([
      tf.loadLayersModel('/models/tag-predictor/output/model.json'),
      fetch('/models/tag-predictor/output/metadata.json').then((r) => {
        if (!r.ok) throw new Error('Failed to load tag predictor metadata.');
        return r.json();
      }),
    ]);

    modelRef.current = model;
    metadataRef.current = metadata;
  }

  async function predictTagIdsFromTitle(title, opts = {}) {
    const {
      threshold = 0.35,
      maxTags = 8,
    } = opts;

    await ensurePredictorLoaded();

    const metadata = metadataRef.current;
    const model = modelRef.current;

    const words = tokenize(title);
    const sequence = words.map((word) => metadata.wordToIndex[word] ?? 1);
    const padded = padSequence(sequence, metadata.maxSequenceLength);

    const probabilities = tf.tidy(() => {
      const inputTensor = tf.tensor2d(
        [padded],
        [1, metadata.maxSequenceLength],
        'int32',
      );

      const prediction = model.predict(inputTensor);
      return Array.from(prediction.dataSync());
    });

    return probabilities
      .map((probability, index) => ({
        tagId: Number(metadata.indexToTagId[index]),
        probability,
      }))
      .filter((x) => Number.isFinite(x.tagId) && x.probability >= threshold)
      .sort((a, b) => b.probability - a.probability)
      .slice(0, maxTags)
      .map((x) => x.tagId);
  }

  useEffect(() => {
    const fetchData = async (q) => {
      const uri = `/api/items/tags?q=${encodeURIComponent(q)}`;

      try {
        const tagsdata = await get(uri);
        setMatches(tagsdata);
      } catch (e) {
        setError(e.message || 'Failed to load tags.');
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);

    if (query !== '') {
      fetchData(query);
    } else {
      setMatches([]);
      setLoading(false);
      setPpv(false);
    }

    handleTagChange(tags);
  }, [query, tags]);

  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  useEffect(() => {
    async function predictAndHydrateTags() {
      const title = String(predictorTitle || '').trim();
      if (!title) return;

      // avoid rerunning endlessly for the exact same title
      if (predictorRanForRef.current === title) return;
      predictorRanForRef.current = title;

      try {
        setError(false);

        const predictedTagIds = await predictTagIdsFromTitle(title, {
          threshold: 0.35,
          maxTags: 8,
        });

        if (!predictedTagIds.length) return;

        // made-up endpoint for now:
        // GET /api/items/tags/by-ids?ids=12,44,81
        const hydratedTags = await get(
          `/api/items/tag/by-ids?ids=${predictedTagIds.join(',')}`,
        );

        if (!Array.isArray(hydratedTags) || !hydratedTags.length) return;

        if (hydratedTags.length > 0 && hydratedTags.map((x) => x.id).join('') !== tags.map((x) => x.id).join('')) {
          toastr.info(t('Tags updated'));
        }

        setTags(hydratedTags);
      } catch (e) {
        setError(e.message || 'Failed to predict tags.');
      }
    }

    if (predictorTitle != null && predictorTitle.trim() !== '') {
      predictAndHydrateTags();
    }
  }, [predictorTitle]);

  let results;

  if (!loading && matches.length > 0) {
    results = (
      <div className="d-flex align-content-start flex-wrap gap-1">
        {matches.map((tag, i) => (
          <Button
            key={tag.id || `${tag.label}_${i}`}
            color="dark"
            className="btn-sm"
            outline
            id={`suggestion_${i}`}
            onClick={() => {
              setPpv(false);

              setTags((prevTags) => mergeUniqueTags(prevTags, [tag]));

              const e = document.getElementById(id);
              e.value = '';
              e.focus();
              setQuery('');
            }}
          >
            {props.t(tag.label)}
          </Button>
        ))}
      </div>
    );
  } else if (loading) {
    results = (
      <span style={{ fontSize: '15px' }} className="mdi mdi-spin mdi-loading" />
    );
  } else {
    results = (
      <span>{props.t('No results.')}</span>
    );

    if (ppv === true) setPpv(false);
  }

  let errorMsg;
  if (error !== false) {
    errorMsg = <Alert color="danger">{error}</Alert>;
  }

  const jsx = (
    <div
      className="tag-selector d-flex align-content-start flex-wrap gap-1 p-2"
      onClick={() => { document.getElementById(id).focus(); }}
    >
      {errorMsg}
      {tags.map((tag, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`frag_${tag.id || tag.label}_${idx}`}>
          <Badge
            color="secondary p-2 bg-soft text-dark"
            className="tag-selector__badge"
          >
            {props.t(tag.label)}
            &nbsp;&nbsp;
            <span
              className="mdi mdi-close-thick"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setTags((prevTags) => prevTags.filter((x) => {
                  if (tag.id != null && x.id != null) {
                    return x.id !== tag.id;
                  }
                  return x.label !== tag.label;
                }));
              }}
            />
          </Badge>
        </React.Fragment>
      ))}
      <input
        type="text"
        id={id}
        onChange={(e) => {
          setQuery(e.target.value);
          setPpv(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setPpv(false);
            setTags((prevTags) => {
              if (query === '') return prevTags;

              // check if typed entire tag
              if (matches.map((x) => x.label).includes(query)) {
                for (let i = 0; i < matches.length; i += 1) {
                  if (matches[i].label === query) {
                    return mergeUniqueTags(prevTags, [matches[i]]);
                  }
                }
              }

              if (allowCreate === false) return prevTags;

              // create new tag
              return [...prevTags, { label: query }];
            });

            e.preventDefault();
            e.target.value = '';
            e.target.focus();
            setQuery('');
          } else if (e.key === 'Backspace') {
            if (e.target.value === '') {
              setTags((prevTags) => {
                const nt = [...prevTags];
                nt.pop();
                return nt;
              });
            }
          }
        }}
        className="tag-selector__input"
        placeholder={props.t('Start typing')}
      />
      <Popover
        placement="bottom"
        isOpen={ppv}
        target={id}
        className="tag-selector__popover"
      >
        <PopoverBody>
          {results}
        </PopoverBody>
      </Popover>
    </div>
  );

  return jsx;
}

TagSelector.defaultProps = {
  initialTags: [],
  onUpdate: () => { },
  allowCreate: false,
  predictorTitle: null,
  t: (t) => t,
};

TagSelector.propTypes = {
  t: PropTypes.any,
  initialTags: PropTypes.array,
  onUpdate: PropTypes.func,
  allowCreate: PropTypes.bool,
  id: PropTypes.string.isRequired,
  predictorTitle: PropTypes.string,
};

export default connect(() => ({}), {})(withTranslation()(TagSelector));
